import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

export type Locale = 'en' | 'zh-CN' | 'zh-TW'
const STORAGE_KEY = 'clash_cards_locale'

const zhCN: Record<string, string> = {
  welcomeTo: '欢迎来到',
  gameAccountsLabel: '游戏账号',
  navigation: '主导航',
  guest: '访客',
  signInSync: '登录并启用云同步',
  guestDataHint: '无需登录即可使用，卡牌数据会保存在此设备。登录后可使用云同步、多账号和换卡功能。',
  addOwned: '增加一张{name}，当前 {count} 张',
  removeOwned: '减少一张{name}',
  editQuantity: '选择要快速录入的数量',
  editing: '正在编辑',
  guestProgressFound: '发现访客卡册',
  guestProgressFoundHint: '是否将本机访客数据导入“{name}”？不会覆盖其他账号。',
  thisAccount: '当前账号',
  importNow: '立即导入',
  importingGuest: '正在导入 {count} 张卡牌…',
  guestImported: '已导入 {count} 张访客卡牌',
  guestImportFailed: '导入失败，本机访客数据已保留，可稍后重试。',
  accountCompletion: '完成 {percent}%',
  accountSwitched: '已切换到 {name}',
  tradeEyebrow: '换卡工作台',
  matchQualityStrong: '高度匹配',
  matchQualityGood: '匹配良好',
  matchQualityPartial: '部分匹配',
  activeToday: '今天活跃',
  activeDaysAgo: '{count} 天前活跃',
  activeRecently: '近期活跃',
  progressEyebrow: '卡册旅程',
  profileEyebrow: '个人中心',
  contactOther: '其他',
  linkCopied: '卡册链接已复制。',
  shareFailed: '无法分享，请手动复制页面地址。',
  authFailed: '登录失败，请检查邮箱或验证码后重试。',
  statusPending: '待处理',
  statusAccepted: '已接受',
  statusDeclined: '已拒绝',
  statusCancelled: '已取消',
  cancelRequest: '取消请求',
  requestCancelled: '换卡请求已取消',
  requestCancelFailed: '无法取消该请求，请刷新后重试。',
  requestsUnavailable: '联系请求暂时无法加载。',
  recentUpdate: '最近更新：{name}',
  exportFailed: '数据导出失败，请稍后重试。',
  exportReady: '数据文件已生成，请选择保存或分享位置。',
  deleteFailed: '账号删除失败，请稍后重试。',
  regionOptional: '地区代码（选填，如 CN）',
  contactRequired: '开启联系方式共享前，请先填写联系方式。',
  accountLimit: '已达到游戏账号数量上限。',
  report: '举报',
  reportPlayer: '举报 {name}',
  reportReason: '举报原因',
  reportDetails: '补充说明（选填）',
  reasonSpam: '垃圾信息',
  reasonHarassment: '骚扰',
  reasonFraud: '欺诈',
  reasonInappropriate: '不当内容',
  reasonOther: '其他',
  submitReport: '提交举报',
  reportSubmitted: '举报已提交，我们会进行审核。',
  reportFailed: '举报提交失败，请稍后重试。',
  maintenance: '服务正在维护，请稍后再试。',
  upgradeRequired: '此版本已过期，请更新后继续。',
  retry: '重试',
  fatalError: '应用遇到意外错误。',
  reloadApp: '重新加载',
  updateReady: '新版本已准备好。',
  updateNow: '立即更新',
  currentAlbum: '当前卡册',
  eventCards: '{count} 张活动卡牌',
  viewDetails: '查看详情 →',
  albumProgress: '卡册进度',
  complete: '完成',
  collectionSection: '典藏分区',
  allCards: '所有卡牌',
  searchCards: '搜索卡牌',
  quickEntry: '快速录入',
  finishEntry: '结束录入',
  quickHint: '连续点击卡牌即可增加拥有数量，修改会自动保存。',
  allStatus: '全部状态',
  owned: '已拥有',
  missing: '未拥有',
  duplicates: '有重复',
  all: '全部',
  elixir: '圣水',
  darkElixir: '黑油',
  builderBase: '夜世界',
  superTroops: '超级兵',
  noCards: '没有符合条件的卡牌',
  clearFilters: '试试清除搜索词或切换状态筛选。',
  collection: '收藏',
  trade: '换卡',
  progress: '进度',
  profile: '我的',
  syncing: '正在同步卡册…',
  syncFailed: '同步失败，请稍后重试',
  synced: '已同步 {count} 条离线修改',
  saving: '正在保存…',
  saved: '已保存',
  queued: '网络不可用，已加入同步队列',
  tradeCenter: '换卡中心',
  tradePrivacy: '只展示模糊地区；双方接受后才交换联系方式。',
  iNeed: '我需要',
  iOffer: '我可提供',
  needCount: '需要 {count} 张',
  offerCount: '可提供 {count} 张',
  findPlayers: '查找互补玩家',
  signInToMatch: '登录后查找玩家',
  noWantedCards: '还没有需要的卡牌',
  noWantedCardsHint: '回到收藏页，将录入维度切换为“需要数量”，再点击卡牌添加需求。',
  noOfferCards: '还没有可提供的卡牌',
  noOfferCardsHint: '可提供数量由“拥有数量 − 保留数量”自动计算，请先在收藏页录入。',
  matchUnavailable: '匹配服务暂时不可用',
  demoSent: '演示请求已发送',
  requestSent: '换卡请求已发送',
  requestFailed: '请求发送失败',
  requestHandleFailed: '处理请求失败',
  noContact: '对方尚未开启联系方式共享',
  contactUnavailable: '联系方式暂不可用',
  complementary: '互补 {count} 张 · {region}',
  regionHidden: '地区未公开',
  sendRequest: '发送请求',
  contactRequests: '联系请求',
  sent: '已发送',
  received: '收到请求',
  accept: '接受',
  decline: '拒绝',
  viewContact: '查看联系方式',
  collectionProgress: '收藏进度',
  seasonMilestone: '本季里程碑',
  completedAlbum: '卡册已全部集齐',
  nextMilestone: '再收集 {count} 张可达到 {percent}%',
  currentCollection: '当前收藏',
  updatesCount: '每次更新卡牌都会即时计入进度',
  profileReadFailed: '设置读取失败',
  contactType: '联系方式类型',
  contact: '联系方式',
  contactPlaceholder: '仅在双方接受换卡后展示',
  shareContact: '允许在换卡达成后展示联系方式',
  notifications: '接收换卡请求通知',
  notificationPermissionDenied: '系统通知权限未开启，通知设置已关闭。',
  saveSettings: '保存设置',
  settingsSaved: '设置已保存',
  saveFailed: '保存失败，请重试',
  demoSettings: '演示模式：设置已保存在当前页面',
  shareAlbum: '分享我的卡册',
  exportData: '导出我的数据',
  privacy: '隐私政策',
  terms: '服务条款',
  help: '帮助中心',
  signOut: '退出登录',
  deleteAccount: '永久删除账号',
  deleteConfirm: '此操作会永久删除登录账号、所有游戏账号和卡册数据，确定继续吗？',
  demoNoDelete: '演示模式不会删除账号',
  shareText: '来看看我的 Clash Cards 卡册',
  ownCount: '拥有数量',
  wantedCount: '需要数量',
  keepCount: '保留数量',
  cardLevel: '卡牌等级',
  closeCard: '关闭卡牌详情',
  decrease: '减少{label}',
  increase: '增加{label}',
  saveClose: '保存并关闭',
  language: '语言',
  languageEnglish: 'English',
  languageSimplified: '简体中文',
  languageTraditional: '繁體中文',
  restoring: '正在恢复登录状态…',
  authIntro: '整理你的卡册进度，找到真正互补的换卡伙伴。',
  email: '邮箱地址',
  otp: '6 位验证码',
  wait: '请稍候…',
  sendCode: '发送验证码',
  loginContinue: '登录并继续',
  changeEmail: '更换邮箱',
  resendCode: '重新发送验证码',
  resendIn: '{count} 秒后可重发',
  codeSent: '验证码已发送，请检查邮箱。',
  agreePrefix: '继续即表示你同意',
  and: '与',
  demoAccount: '演示账号',
  loading: '加载中',
  createAccount: '创建账号',
  accountServiceUnavailable: '账号服务尚未部署，请稍后重试。',
  chooseAccount: '选择游戏账号',
  close: '关闭',
  noPlayerTag: '未填写玩家标签',
  currentAccount: '当前账号',
  edit: '编辑 {name}',
  defaultAccount: '设 {name} 为默认',
  delete: '删除 {name}',
  firstAccount: '先创建你的第一个游戏账号',
  firstAccountHint: '之后就能录入卡牌、跟踪进度并查找换卡伙伴。',
  editAccount: '编辑游戏账号',
  addAccount: '添加游戏账号',
  accountName: '账号名称',
  playerTagOptional: '玩家标签（选填）',
  savingAccount: '保存中…',
  saveChanges: '保存修改',
  createFailed: '创建失败，请检查玩家标签是否重复。',
  defaultFailed: '设置默认账号失败。',
  deleteGameConfirm: '确定删除这个游戏账号及其卡册数据吗？',
  deleteGameFailed: '删除账号失败。',
  demoNoCreate: '演示模式不会创建真实账号。',
}

const traditionalCharacterMap: Record<string, string> = {
  账: '帳',
  张: '張',
  户: '戶',
  这: '這',
  为: '為',
  后: '後',
  发: '發',
  开: '開',
  录: '錄',
  数: '數',
  据: '據',
  载: '載',
  络: '絡',
  队: '隊',
  续: '續',
  击: '擊',
  拥: '擁',
  动: '動',
  时: '時',
  间: '間',
  过: '過',
  与: '與',
  详: '詳',
  类: '類',
  别: '別',
  总: '總',
  进: '進',
  当: '當',
  经: '經',
  编: '編',
  辑: '輯',
  现: '現',
  实: '實',
  该: '該',
  复: '復',
  单: '單',
  换: '換',
  隐: '隱',
  联: '聯',
  个: '個',
  页: '頁',
  签: '籤',
  条: '條',
  务: '務',
  删: '刪',
  号: '號',
  输: '輸',
  码: '碼',
  验: '驗',
  证: '證',
  邮: '郵',
  创: '創',
  认: '認',
  设: '設',
  败: '敗',
  检: '檢',
  标: '標',
  请: '請',
  选: '選',
  择: '擇',
  区: '區',
  启: '啟',
  云: '雲',
  导: '導',
  机: '機',
  会: '會',
  图: '圖',
  显: '顯',
  样: '樣',
  写: '寫',
  应: '應',
  览: '覽',
  筛: '篩',
  词: '詞',
  状: '狀',
  态: '態',
  关: '關',
  暂: '暫',
  补: '補',
  传: '傳',
  读: '讀',
  线: '線',
  处: '處',
  优: '優',
  误: '誤',
  仅: '僅',
  边: '邊',
  点: '點',
  将: '將',
  长: '長',
  无: '無',
  获: '獲',
  让: '讓',
  较: '較',
  违: '違',
  规: '規',
  审: '審',
  弹: '彈',
  预: '預',
  计: '計',
  额: '額',
  终: '終',
  须: '須',
  网: '網',
  觉: '覺',
  确: '確',
  释: '釋',
  锁: '鎖',
  独: '獨',
  资: '資',
  讯: '訊',
  试: '試',
  册: '冊',
  内: '內',
  访: '訪',
  游: '遊',
  戏: '戲',
  毕: '畢',
  门: '門',
  汇: '匯',
  强: '強',
  诉: '訴',
  义: '義',
  际: '際',
  种: '種',
  尽: '盡',
  频: '頻',
  调: '調',
  来: '來',
  说: '說',
  块: '塊',
  报: '報',
  馈: '饋',
  链: '鏈',
  台: '臺',
  产: '產',
  广: '廣',
  从: '從',
  还: '還',
  离: '離',
  储: '儲',
  递: '遞',
  权: '權',
  绑: '綁',
  订: '訂',
  阅: '閱',
  扫: '掃',
  级: '級',
  满: '滿',
  继: '繼',
  转: '轉',
  见: '見',
  题: '題',
  维: '維',
  运: '運',
  营: '營',
  组: '組',
  织: '織',
  虽: '雖',
  随: '隨',
  统: '統',
  库: '庫',
  刚: '剛',
  层: '層',
  值: '值',
  护: '護',
  则: '則',
  损: '損',
  绝: '絕',
  识: '識',
  拟: '擬',
  临: '臨',
  达: '達',
  许: '許',
  扰: '擾',
  诈: '詐',
  准: '準',
  备: '備',
  结: '結',
  圣: '聖',
  双: '雙',
  齐: '齊',
  减: '減',
  闭: '閉',
  语: '語',
  简: '簡',
  体: '體',
  踪: '蹤',
  称: '稱',
}
const toTraditional = (value: string) =>
  Array.from(value, (character) => traditionalCharacterMap[character] ?? character).join('')
const zhTWBase = Object.fromEntries(
  Object.entries(zhCN).map(([key, value]) => [key, toTraditional(value)]),
)

const zhTW: Record<string, string> = {
  ...zhTWBase,
  welcomeTo: '歡迎來到',
  gameAccountsLabel: '遊戲帳號',
  navigation: '主導覽',
  guest: '訪客',
  signInSync: '登入並啟用雲端同步',
  guestDataHint:
    '無需登入即可使用，卡牌資料會儲存在此裝置。登入後可使用雲端同步、多帳號和換卡功能。',
  addOwned: '增加一張{name}，目前 {count} 張',
  removeOwned: '減少一張{name}',
  editQuantity: '選擇要快速錄入的數量',
  editing: '正在編輯',
  guestProgressFound: '發現訪客卡冊',
  guestProgressFoundHint: '是否將本機訪客資料匯入「{name}」？不會覆蓋其他帳號。',
  thisAccount: '目前帳號',
  importNow: '立即匯入',
  importingGuest: '正在匯入 {count} 張卡牌…',
  guestImported: '已匯入 {count} 張訪客卡牌',
  guestImportFailed: '匯入失敗，本機訪客資料已保留，可稍後重試。',
  accountCompletion: '完成 {percent}%',
  accountSwitched: '已切換到 {name}',
  tradeEyebrow: '換卡工作台',
  matchQualityStrong: '高度配對',
  matchQualityGood: '配對良好',
  matchQualityPartial: '部分配對',
  activeToday: '今天活躍',
  activeDaysAgo: '{count} 天前活躍',
  activeRecently: '近期活躍',
  progressEyebrow: '卡冊旅程',
  profileEyebrow: '個人中心',
  contactOther: '其他',
  linkCopied: '卡冊連結已複製。',
  shareFailed: '無法分享，請手動複製頁面網址。',
  agreePrefix: '繼續即表示你同意',
  and: '與',
  close: '關閉',
  all: '全部',
  elixir: '聖水',
  darkElixir: '黑油',
  superTroops: '超級兵',
  authFailed: '登入失敗，請檢查電子郵件或驗證碼後重試。',
  statusPending: '待處理',
  statusAccepted: '已接受',
  statusDeclined: '已拒絕',
  statusCancelled: '已取消',
  cancelRequest: '取消請求',
  requestCancelled: '換卡請求已取消',
  requestCancelFailed: '無法取消該請求，請重新整理後再試。',
  requestsUnavailable: '聯絡請求暫時無法載入。',
  requestFailed: '請求傳送失敗',
  requestHandleFailed: '處理請求失敗',
  recentUpdate: '最近更新：{name}',
  exportFailed: '資料匯出失敗，請稍後再試。',
  exportReady: '資料檔案已產生，請選擇儲存或分享位置。',
  deleteFailed: '帳號刪除失敗，請稍後再試。',
  regionOptional: '地區代碼（選填，如 TW）',
  contactRequired: '開啟聯絡方式分享前，請先填寫聯絡方式。',
  accountLimit: '已達到遊戲帳號數量上限。',
  report: '檢舉',
  reportPlayer: '檢舉 {name}',
  reportReason: '檢舉原因',
  reportDetails: '補充說明（選填）',
  reasonSpam: '垃圾訊息',
  reasonHarassment: '騷擾',
  reasonFraud: '詐騙',
  reasonInappropriate: '不當內容',
  reasonOther: '其他',
  submitReport: '提交檢舉',
  reportSubmitted: '檢舉已提交，我們會進行審核。',
  reportFailed: '檢舉提交失敗，請稍後再試。',
  maintenance: '服務正在維護，請稍後再試。',
  upgradeRequired: '此版本已過期，請更新後繼續。',
  retry: '重試',
  fatalError: '應用程式遇到意外錯誤。',
  reloadApp: '重新載入',
  updateReady: '新版本已準備好。',
  updateNow: '立即更新',
  currentAlbum: '目前卡冊',
  eventCards: '{count} 張活動卡牌',
  viewDetails: '查看詳情 →',
  albumProgress: '卡冊進度',
  collectionSection: '典藏分區',
  allCards: '所有卡牌',
  searchCards: '搜尋卡牌',
  quickEntry: '快速錄入',
  finishEntry: '結束錄入',
  quickHint: '連續點擊卡牌即可增加擁有數量，修改會自動儲存。',
  allStatus: '全部狀態',
  owned: '已擁有',
  missing: '未擁有',
  duplicates: '有重複',
  builderBase: '夜世界',
  noCards: '沒有符合條件的卡牌',
  clearFilters: '試試清除搜尋詞或切換狀態篩選。',
  collection: '收藏',
  trade: '換卡',
  progress: '進度',
  profile: '我的',
  syncing: '正在同步卡冊…',
  syncFailed: '同步失敗，請稍後重試',
  saving: '正在儲存…',
  saved: '已儲存',
  queued: '網路不可用，已加入同步佇列',
  tradeCenter: '換卡中心',
  tradePrivacy: '只顯示模糊地區；雙方接受後才交換聯絡方式。',
  iNeed: '我需要',
  iOffer: '我可提供',
  findPlayers: '尋找互補玩家',
  signInToMatch: '登入後尋找玩家',
  noWantedCards: '還沒有需要的卡牌',
  noWantedCardsHint: '返回收藏頁，將錄入維度切換為「需要數量」，再點擊卡牌新增需求。',
  noOfferCards: '還沒有可提供的卡牌',
  noOfferCardsHint: '可提供數量由「擁有數量 − 保留數量」自動計算，請先在收藏頁錄入。',
  sendRequest: '傳送請求',
  contactRequests: '聯絡請求',
  received: '收到請求',
  accept: '接受',
  decline: '拒絕',
  viewContact: '查看聯絡方式',
  collectionProgress: '收藏進度',
  seasonMilestone: '本季里程碑',
  completedAlbum: '卡冊已全部集齊',
  currentCollection: '目前收藏',
  notificationPermissionDenied: '系統通知權限未開啟，通知設定已關閉。',
  contactType: '聯絡方式類型',
  contact: '聯絡方式',
  saveSettings: '儲存設定',
  settingsSaved: '設定已儲存',
  privacy: '隱私政策',
  terms: '服務條款',
  help: '幫助中心',
  signOut: '登出',
  deleteAccount: '永久刪除帳號',
  ownCount: '擁有數量',
  wantedCount: '需要數量',
  keepCount: '保留數量',
  cardLevel: '卡牌等級',
  saveClose: '儲存並關閉',
  language: '語言',
  restoring: '正在恢復登入狀態…',
  authIntro: '整理你的卡冊進度，找到真正互補的換卡夥伴。',
  email: '電子郵件地址',
  sendCode: '傳送驗證碼',
  loginContinue: '登入並繼續',
  changeEmail: '更換電子郵件',
  resendCode: '重新傳送驗證碼',
  resendIn: '{count} 秒後可重傳',
  chooseAccount: '選擇遊戲帳號',
  createAccount: '建立帳號',
  accountName: '帳號名稱',
  addAccount: '新增遊戲帳號',
  editAccount: '編輯遊戲帳號',
}

const en: Record<string, string> = {
  welcomeTo: 'WELCOME TO',
  gameAccountsLabel: 'GAME ACCOUNTS',
  navigation: 'Primary navigation',
  guest: 'Guest',
  signInSync: 'Sign in for cloud sync',
  guestDataHint:
    'Use the app without signing in. Card data stays on this device. Sign in for cloud sync, multiple accounts and trading.',
  addOwned: 'Add one {name}, currently {count}',
  removeOwned: 'Remove one {name}',
  editQuantity: 'Choose the quantity to edit',
  editing: 'Editing',
  guestProgressFound: 'Guest album found',
  guestProgressFoundHint:
    'Import the guest data on this device into “{name}”? Other accounts are not changed.',
  thisAccount: 'this account',
  importNow: 'Import now',
  importingGuest: 'Importing {count} cards…',
  guestImported: 'Imported {count} guest cards',
  guestImportFailed: 'Import failed. Your guest data is still on this device so you can retry.',
  accountCompletion: '{percent}% complete',
  accountSwitched: 'Switched to {name}',
  tradeEyebrow: 'TRADE DESK',
  matchQualityStrong: 'Strong match',
  matchQualityGood: 'Good match',
  matchQualityPartial: 'Partial match',
  activeToday: 'Active today',
  activeDaysAgo: 'Active {count} days ago',
  activeRecently: 'Recently active',
  progressEyebrow: 'ALBUM JOURNEY',
  profileEyebrow: 'PROFILE',
  contactOther: 'Other',
  linkCopied: 'Album link copied.',
  shareFailed: 'Unable to share. Copy the page address manually.',
  authFailed: 'Sign-in failed. Check your email or verification code and try again.',
  statusPending: 'Pending',
  statusAccepted: 'Accepted',
  statusDeclined: 'Declined',
  statusCancelled: 'Cancelled',
  cancelRequest: 'Cancel request',
  requestCancelled: 'Trade request cancelled',
  requestCancelFailed: 'Could not cancel this request. Refresh and try again.',
  requestsUnavailable: 'Could not load contact requests.',
  recentUpdate: 'Recently updated: {name}',
  exportFailed: 'Could not export your data. Try again later.',
  exportReady: 'Your data file is ready. Choose where to save or share it.',
  deleteFailed: 'Could not delete your account. Try again later.',
  regionOptional: 'Region code (optional, e.g. US)',
  contactRequired: 'Add contact details before enabling contact sharing.',
  accountLimit: 'You have reached the game account limit.',
  report: 'Report',
  reportPlayer: 'Report {name}',
  reportReason: 'Reason',
  reportDetails: 'Additional details (optional)',
  reasonSpam: 'Spam',
  reasonHarassment: 'Harassment',
  reasonFraud: 'Fraud',
  reasonInappropriate: 'Inappropriate content',
  reasonOther: 'Other',
  submitReport: 'Submit report',
  reportSubmitted: 'Report submitted for review.',
  reportFailed: 'Could not submit report. Try again later.',
  maintenance: 'The service is under maintenance. Try again later.',
  upgradeRequired: 'This version is no longer supported. Update to continue.',
  retry: 'Retry',
  fatalError: 'The app ran into an unexpected error.',
  reloadApp: 'Reload app',
  updateReady: 'A new version is ready.',
  updateNow: 'Update',
  currentAlbum: 'Current album',
  eventCards: '{count} event cards',
  viewDetails: 'View details →',
  albumProgress: 'Album progress',
  complete: 'complete',
  collectionSection: 'COLLECTION SECTIONS',
  allCards: 'All cards',
  searchCards: 'Search cards',
  quickEntry: 'Quick entry',
  finishEntry: 'Finish entry',
  quickHint: 'Tap cards repeatedly to increase owned count. Changes save automatically.',
  allStatus: 'All status',
  owned: 'Owned',
  missing: 'Missing',
  duplicates: 'Duplicates',
  all: 'All',
  elixir: 'Elixir',
  darkElixir: 'Dark Elixir',
  builderBase: 'Builder Base',
  superTroops: 'Super Troops',
  noCards: 'No matching cards',
  clearFilters: 'Clear the search or change the status filter.',
  collection: 'Collection',
  trade: 'Trade',
  progress: 'Progress',
  profile: 'Profile',
  syncing: 'Syncing album…',
  syncFailed: 'Sync failed. Try again later.',
  synced: 'Synced {count} offline changes',
  saving: 'Saving…',
  saved: 'Saved',
  queued: 'Offline. Added to the sync queue.',
  tradeCenter: 'Trade center',
  tradePrivacy:
    'Only an approximate region is shown. Contact details are shared after both players accept.',
  iNeed: 'I need',
  iOffer: 'I can offer',
  needCount: 'Need {count}',
  offerCount: 'Can offer {count}',
  findPlayers: 'Find complementary players',
  signInToMatch: 'Sign in to find players',
  noWantedCards: 'No wanted cards yet',
  noWantedCardsHint:
    'Go to Collection, switch the editing field to Wanted, then tap cards to add them.',
  noOfferCards: 'No cards to offer yet',
  noOfferCardsHint:
    'Offers are calculated as Owned minus Keep. Enter those quantities in Collection first.',
  matchUnavailable: 'Matching is temporarily unavailable',
  demoSent: 'Demo request sent',
  requestSent: 'Trade request sent',
  requestFailed: 'Could not send request',
  requestHandleFailed: 'Could not update request',
  noContact: 'This player has not enabled contact sharing',
  contactUnavailable: 'Contact details are unavailable',
  complementary: '{count} complementary cards · {region}',
  regionHidden: 'Region hidden',
  sendRequest: 'Send request',
  contactRequests: 'Requests',
  sent: 'Sent',
  received: 'Received',
  accept: 'Accept',
  decline: 'Decline',
  viewContact: 'View contact',
  collectionProgress: 'Collection progress',
  seasonMilestone: 'Season milestone',
  completedAlbum: 'Album complete',
  nextMilestone: 'Collect {count} more to reach {percent}%',
  currentCollection: 'Current collection',
  updatesCount: 'Card updates are reflected here instantly',
  profileReadFailed: 'Could not load settings',
  contactType: 'Contact type',
  contact: 'Contact details',
  contactPlaceholder: 'Only shown after both players accept a trade',
  shareContact: 'Share contact details after a trade is accepted',
  notifications: 'Receive trade request notifications',
  notificationPermissionDenied:
    'System notification permission was not granted, so notifications were turned off.',
  saveSettings: 'Save settings',
  settingsSaved: 'Settings saved',
  saveFailed: 'Could not save. Try again.',
  demoSettings: 'Demo mode: settings are stored on this page',
  shareAlbum: 'Share my album',
  exportData: 'Export my data',
  privacy: 'Privacy Policy',
  terms: 'Terms of Service',
  help: 'Help Center',
  signOut: 'Sign out',
  deleteAccount: 'Permanently delete account',
  deleteConfirm: 'This permanently deletes your login, game accounts and album data. Continue?',
  demoNoDelete: 'Demo mode does not delete accounts',
  shareText: 'Take a look at my Clash Cards album',
  ownCount: 'Owned',
  wantedCount: 'Wanted',
  keepCount: 'Keep',
  cardLevel: 'Card level',
  closeCard: 'Close card details',
  decrease: 'Decrease {label}',
  increase: 'Increase {label}',
  saveClose: 'Save and close',
  language: 'Language',
  languageEnglish: 'English',
  languageSimplified: '简体中文',
  languageTraditional: '繁體中文',
  restoring: 'Restoring your session…',
  authIntro: 'Track your album and find players with complementary cards.',
  email: 'Email address',
  otp: '6-digit code',
  wait: 'Please wait…',
  sendCode: 'Send code',
  loginContinue: 'Sign in and continue',
  changeEmail: 'Change email',
  resendCode: 'Resend code',
  resendIn: 'Resend in {count}s',
  codeSent: 'Verification code sent. Check your email.',
  agreePrefix: 'By continuing, you agree to the',
  and: 'and',
  demoAccount: 'Demo account',
  loading: 'Loading',
  createAccount: 'Create account',
  accountServiceUnavailable: 'Account service is not deployed yet. Try again later.',
  chooseAccount: 'Choose game account',
  close: 'Close',
  noPlayerTag: 'No player tag',
  currentAccount: 'Current account',
  edit: 'Edit {name}',
  defaultAccount: 'Make {name} default',
  delete: 'Delete {name}',
  firstAccount: 'Create your first game account',
  firstAccountHint: 'Then you can enter cards, track progress and find trade partners.',
  editAccount: 'Edit game account',
  addAccount: 'Add game account',
  accountName: 'Account name',
  playerTagOptional: 'Player tag (optional)',
  savingAccount: 'Saving…',
  saveChanges: 'Save changes',
  createFailed: 'Could not create account. Check whether the player tag is already used.',
  defaultFailed: 'Could not set default account.',
  deleteGameConfirm: 'Delete this game account and all of its album data?',
  deleteGameFailed: 'Could not delete game account.',
  demoNoCreate: 'Demo mode does not create real accounts.',
}

const dictionaries: Record<Locale, Record<string, string>> = {
  en,
  'zh-CN': zhCN,
  'zh-TW': zhTW,
}
export function resolveLocale(value: string | null): Locale {
  return value === 'zh-CN' || value === 'zh-TW' || value === 'en' ? value : 'en'
}
export function translate(
  locale: Locale,
  key: string,
  values: Record<string, string | number> = {},
) {
  return Object.entries(values).reduce(
    (text, [name, value]) => text.replaceAll(`{${name}}`, String(value)),
    dictionaries[locale][key] ?? en[key] ?? key,
  )
}
type I18nValue = {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string, values?: Record<string, string | number>) => string
}
const I18nContext = createContext<I18nValue | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() =>
    resolveLocale(localStorage.getItem(STORAGE_KEY)),
  )
  const setLocale = (next: Locale) => {
    localStorage.setItem(STORAGE_KEY, next)
    setLocaleState(next)
  }
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])
  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t: (key: string, values: Record<string, string | number> = {}) =>
        translate(locale, key, values),
    }),
    [locale],
  )
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}
export function useI18n() {
  const value = useContext(I18nContext)
  if (!value) throw new Error('I18nProvider missing')
  return value
}
